import type { LocalTranscriptionReadiness } from '@electron/shared/meetings.types';

export function localTranscriptionProgressFraction(
  readiness: LocalTranscriptionReadiness
): number | null {
  if (readiness.state !== 'preparing') return null;
  const fraction = readiness.progress?.fraction;
  if (typeof fraction !== 'number' || !Number.isFinite(fraction)) return null;
  return Math.min(Math.max(fraction, 0), 1);
}

export function localTranscriptionProgressFileCount(
  readiness: LocalTranscriptionReadiness
): string | null {
  if (readiness.state !== 'preparing') return null;
  const { completedFiles, totalFiles } = readiness.progress ?? {};
  if (typeof completedFiles !== 'number' || typeof totalFiles !== 'number') {
    return null;
  }
  return `${Math.min(completedFiles, totalFiles)} of ${totalFiles} files`;
}

export function formatLocalTranscriptionReadiness(
  readiness: LocalTranscriptionReadiness
): string {
  switch (readiness.state) {
    case 'not_started':
      return `${readiness.model.displayName} is not prepared yet; Remote Transcription will be used until preparation finishes`;
    case 'preparing': {
      const fraction = localTranscriptionProgressFraction(readiness);
      const progressText =
        fraction === null ? readiness.phase : `${Math.floor(fraction * 100)}%`;
      const fileCount = localTranscriptionProgressFileCount(readiness);
      const detail =
        fileCount === null ? progressText : `${progressText}, ${fileCount}`;
      return `Preparing ${readiness.model.displayName}: ${detail}`;
    }
    case 'cache_ready':
    case 'disk_cached':
      return `${readiness.model.displayName} is downloaded; Local Transcription will load it before use`;
    case 'resident_ready':
      return `${readiness.model.displayName} is ready for the next Local meeting`;
    case 'ready':
      return `${readiness.model.displayName} is ready for this Local meeting`;
    case 'failed':
      return `Local Transcription failed: ${readiness.message}`;
    case 'unavailable':
      return `Local Transcription unavailable: ${readiness.message}`;
  }
}
